import { readBeginnerTimeMilliseconds } from '@skyrover/blockly-blocks';
import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment } from './arduino-generator.js';
import {
  beginnerLedState,
  type PixelConfig,
  type RgbPinConfig,
} from './beginner-led-state.js';

const PIXEL_STRIP_NAME = 'skyroverPixels';
const RGB_BRIGHTNESS_VAR = 'skyroverRgbBrightness';

const PRESET_COLORS: Readonly<Record<string, readonly [number, number, number]>> = {
  RED: [255, 0, 0],
  GREEN: [0, 255, 0],
  BLUE: [0, 0, 255],
  YELLOW: [255, 255, 0],
  CYAN: [0, 255, 255],
  MAGENTA: [255, 0, 255],
  WHITE: [255, 255, 255],
  ORANGE: [255, 165, 0],
  PURPLE: [128, 0, 128],
  PINK: [255, 105, 180],
};

const DEFAULT_RGB_PINS: RgbPinConfig = {
  redPin: '9',
  greenPin: '10',
  bluePin: '11',
};

const DEFAULT_PIXEL_CONFIG: PixelConfig = {
  dataPin: '6',
  ledCount: 8,
};

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function clamp255(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.min(255, Math.max(0, Math.round(numeric)));
}

function clampPercent(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(numeric)));
}

function percentToPwm(percent: number): number {
  return clamp255((clampPercent(percent) * 255) / 100);
}

function pinExpression(pin: string): string {
  return pin === 'LED_BUILTIN' ? 'LED_BUILTIN' : pin;
}

function ensureOutputPin(pin: string): void {
  const pinCode = pinExpression(pin);
  arduinoGenerator.addSetupOnce(`beginner-output-${pinCode}`, `pinMode(${pinCode}, OUTPUT);`);
}

function resolveColorFromBlock(block: Block): { r: number; g: number; b: number } {
  const preset = String(block.getFieldValue('COLOR') ?? 'RED');
  if (preset === 'CUSTOM') {
    const fromPicker = hexToRgb(String(block.getFieldValue('CUSTOM_COLOUR') ?? ''));
    if (fromPicker) {
      return fromPicker;
    }
    return { r: 255, g: 0, b: 0 };
  }
  const color = PRESET_COLORS[preset];
  if (color) {
    return { r: color[0], g: color[1], b: color[2] };
  }
  return { r: 255, g: 0, b: 0 };
}

function applyRgbBrightness(r: number, g: number, b: number): { r: number; g: number; b: number } {
  const scale = beginnerLedState.rgbBrightnessPercent / 100;
  return {
    r: clamp255(r * scale),
    g: clamp255(g * scale),
    b: clamp255(b * scale),
  };
}

function getRgbPins(): RgbPinConfig {
  return beginnerLedState.rgbPins ?? DEFAULT_RGB_PINS;
}

function ensureRgbGlobals(): void {
  arduinoGenerator.addGlobal(`uint8_t ${RGB_BRIGHTNESS_VAR} = 100;`);
}

function ensureRgbPinsConfigured(pins: RgbPinConfig): void {
  ensureRgbGlobals();
  ensureOutputPin(pins.redPin);
  ensureOutputPin(pins.greenPin);
  ensureOutputPin(pins.bluePin);
}

function writeRgbColor(r: number, g: number, b: number, pins: RgbPinConfig): string {
  const scaled = applyRgbBrightness(r, g, b);
  return [
    `analogWrite(${pinExpression(pins.redPin)}, ${scaled.r});`,
    `analogWrite(${pinExpression(pins.greenPin)}, ${scaled.g});`,
    `analogWrite(${pinExpression(pins.bluePin)}, ${scaled.b});`,
    '',
  ].join('\n');
}

function ensurePixelSupport(config: PixelConfig): void {
  arduinoGenerator.addInclude('#include <Adafruit_NeoPixel.h>');
  arduinoGenerator.addGlobal(
    `Adafruit_NeoPixel ${PIXEL_STRIP_NAME}(${config.ledCount}, ${pinExpression(config.dataPin)}, NEO_GRB + NEO_KHZ800);`,
  );
  arduinoGenerator.addGlobal(
    [
      'uint32_t skyroverPixelWheel(byte wheelPos) {',
      '  wheelPos = 255 - wheelPos;',
      '  if (wheelPos < 85) {',
      '    return skyroverPixels.Color(255 - wheelPos * 3, 0, wheelPos * 3);',
      '  }',
      '  if (wheelPos < 170) {',
      '    wheelPos -= 85;',
      '    return skyroverPixels.Color(0, wheelPos * 3, 255 - wheelPos * 3);',
      '  }',
      '  wheelPos -= 170;',
      '  return skyroverPixels.Color(wheelPos * 3, 255 - wheelPos * 3, 0);',
      '}',
    ].join('\n'),
  );
  arduinoGenerator.addSetupOnce(
    `beginner-pixel-${config.dataPin}-${config.ledCount}`,
    [
      `${PIXEL_STRIP_NAME}.begin();`,
      `${PIXEL_STRIP_NAME}.setBrightness(${percentToPwm(beginnerLedState.pixelBrightnessPercent)});`,
      `${PIXEL_STRIP_NAME}.show();`,
    ].join('\n'),
  );
}

function getPixelConfig(): PixelConfig {
  return beginnerLedState.pixelConfig ?? DEFAULT_PIXEL_CONFIG;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  const value = match?.[1];
  if (!value) {
    return null;
  }
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function resolvePixelColor(block: Block): { r: number; g: number; b: number } {
  return resolveColorFromBlock(block);
}

export function registerBeginnerLedBlockGenerators(): void {
  arduinoGenerator.forBlock['beginner_std_led_turn'] = (block: Block) => {
    const action = String(block.getFieldValue('ACTION') ?? 'ON');
    const pin = String(block.getFieldValue('PIN') ?? '13');
    ensureOutputPin(pin);
    const pinCode = pinExpression(pin);

    let actionCode = `digitalWrite(${pinCode}, HIGH);`;
    if (action === 'OFF') {
      actionCode = `digitalWrite(${pinCode}, LOW);`;
    } else if (action === 'TOGGLE') {
      actionCode = `digitalWrite(${pinCode}, !digitalRead(${pinCode}));`;
    }

    return statementPrefix(block, `${actionCode}\n`);
  };

  arduinoGenerator.forBlock['beginner_std_led_blink'] = (block: Block) => {
    const pin = String(block.getFieldValue('PIN') ?? '13');
    const onMs = readBeginnerTimeMilliseconds(block, 'ON_TIME');
    const offMs = readBeginnerTimeMilliseconds(block, 'OFF_TIME');
    ensureOutputPin(pin);
    const pinCode = pinExpression(pin);

    return statementPrefix(
      block,
      [
        `digitalWrite(${pinCode}, HIGH);`,
        `delay(${onMs});`,
        `digitalWrite(${pinCode}, LOW);`,
        `delay(${offMs});`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_std_led_brightness'] = (block: Block) => {
    const pin = String(block.getFieldValue('PIN') ?? '13');
    const brightness = clampPercent(block.getFieldValue('BRIGHTNESS'));
    ensureOutputPin(pin);
    const pinCode = pinExpression(pin);
    return statementPrefix(block, `analogWrite(${pinCode}, ${percentToPwm(brightness)});\n`);
  };

  arduinoGenerator.forBlock['beginner_rgb_configure'] = (block: Block) => {
    const pins: RgbPinConfig = {
      redPin: String(block.getFieldValue('RED_PIN') ?? DEFAULT_RGB_PINS.redPin),
      greenPin: String(block.getFieldValue('GREEN_PIN') ?? DEFAULT_RGB_PINS.greenPin),
      bluePin: String(block.getFieldValue('BLUE_PIN') ?? DEFAULT_RGB_PINS.bluePin),
    };
    beginnerLedState.rgbPins = pins;
    ensureRgbPinsConfigured(pins);
    return statementPrefix(
      block,
      [
        `// RGB LED configured on pins ${pins.redPin}, ${pins.greenPin}, ${pins.bluePin}`,
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_rgb_set_color'] = (block: Block) => {
    const pins = getRgbPins();
    ensureRgbPinsConfigured(pins);
    const { r, g, b } = resolveColorFromBlock(block);
    return statementPrefix(block, writeRgbColor(r, g, b, pins));
  };

  arduinoGenerator.forBlock['beginner_rgb_turn_off'] = (block: Block) => {
    const pins = getRgbPins();
    ensureRgbPinsConfigured(pins);
    return statementPrefix(block, writeRgbColor(0, 0, 0, pins));
  };

  arduinoGenerator.forBlock['beginner_rgb_brightness'] = (block: Block) => {
    const brightness = clampPercent(block.getFieldValue('BRIGHTNESS'));
    beginnerLedState.rgbBrightnessPercent = brightness;
    ensureRgbGlobals();
    return statementPrefix(block, `${RGB_BRIGHTNESS_VAR} = ${brightness};\n`);
  };

  arduinoGenerator.forBlock['beginner_pixel_configure'] = (block: Block) => {
    const config: PixelConfig = {
      dataPin: String(block.getFieldValue('DATA_PIN') ?? DEFAULT_PIXEL_CONFIG.dataPin),
      ledCount: Math.max(1, Math.round(Number(block.getFieldValue('LED_COUNT') ?? 8))),
    };
    beginnerLedState.pixelConfig = config;
    ensurePixelSupport(config);
    return statementPrefix(
      block,
      [`// Pixel LED strip configured on pin ${config.dataPin}`, ''].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_pixel_set_color'] = (block: Block) => {
    const config = getPixelConfig();
    ensurePixelSupport(config);
    const pixel = clamp255(block.getFieldValue('PIXEL'));
    const { r, g, b } = resolvePixelColor(block);
    return statementPrefix(
      block,
      `${PIXEL_STRIP_NAME}.setPixelColor(${pixel}, ${PIXEL_STRIP_NAME}.Color(${r}, ${g}, ${b}));\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_pixel_set_all'] = (block: Block) => {
    const config = getPixelConfig();
    ensurePixelSupport(config);
    const { r, g, b } = resolvePixelColor(block);
    return statementPrefix(
      block,
      [
        `for (int i = 0; i < ${PIXEL_STRIP_NAME}.numPixels(); i++) {`,
        `  ${PIXEL_STRIP_NAME}.setPixelColor(i, ${PIXEL_STRIP_NAME}.Color(${r}, ${g}, ${b}));`,
        '}',
        '',
      ].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_pixel_clear'] = (block: Block) => {
    const config = getPixelConfig();
    ensurePixelSupport(config);
    return statementPrefix(block, `${PIXEL_STRIP_NAME}.clear();\n`);
  };

  arduinoGenerator.forBlock['beginner_pixel_show'] = (block: Block) => {
    const config = getPixelConfig();
    ensurePixelSupport(config);
    return statementPrefix(block, `${PIXEL_STRIP_NAME}.show();\n`);
  };

  arduinoGenerator.forBlock['beginner_pixel_brightness'] = (block: Block) => {
    const config = getPixelConfig();
    const brightness = clampPercent(block.getFieldValue('BRIGHTNESS'));
    beginnerLedState.pixelBrightnessPercent = brightness;
    ensurePixelSupport(config);
    return statementPrefix(
      block,
      `${PIXEL_STRIP_NAME}.setBrightness(${percentToPwm(brightness)});\n`,
    );
  };

  arduinoGenerator.forBlock['beginner_pixel_effect'] = (block: Block) => {
    const config = getPixelConfig();
    ensurePixelSupport(config);
    const effect = String(block.getFieldValue('EFFECT') ?? 'RAINBOW');
    const strip = PIXEL_STRIP_NAME;

    let effectCode: string[];
    switch (effect) {
      case 'COLOR_WIPE':
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `  ${strip}.setPixelColor(i, ${strip}.Color(0, 150, 255));`,
          `  ${strip}.show();`,
          '  delay(40);',
          '}',
          '',
        ];
        break;
      case 'CHASE':
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `  ${strip}.clear();`,
          `  ${strip}.setPixelColor(i, ${strip}.Color(0, 150, 255));`,
          `  ${strip}.show();`,
          '  delay(50);',
          '}',
          '',
        ];
        break;
      case 'THEATER_CHASE':
        effectCode = [
          'for (int j = 0; j < 10; j++) {',
          '  for (int q = 0; q < 3; q++) {',
          `    for (int i = 0; i < ${strip}.numPixels(); i += 3) {`,
          `      ${strip}.setPixelColor(i + q, ${strip}.Color(255, 80, 0));`,
          '    }',
          `    ${strip}.show();`,
          '    delay(60);',
          `    for (int i = 0; i < ${strip}.numPixels(); i += 3) {`,
          `      ${strip}.setPixelColor(i + q, 0);`,
          '    }',
          '  }',
          '}',
          '',
        ];
        break;
      case 'BLINK':
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `  ${strip}.setPixelColor(i, ${strip}.Color(255, 255, 255));`,
          '}',
          `${strip}.show();`,
          'delay(200);',
          `${strip}.clear();`,
          `${strip}.show();`,
          'delay(200);',
          '',
        ];
        break;
      case 'BREATHE':
        effectCode = [
          'for (int b = 0; b <= 255; b += 5) {',
          `  ${strip}.setBrightness(b);`,
          `  for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `    ${strip}.setPixelColor(i, ${strip}.Color(0, 120, 255));`,
          '  }',
          `  ${strip}.show();`,
          '  delay(12);',
          '}',
          'for (int b = 255; b >= 0; b -= 5) {',
          `  ${strip}.setBrightness(b);`,
          `  for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `    ${strip}.setPixelColor(i, ${strip}.Color(0, 120, 255));`,
          '  }',
          `  ${strip}.show();`,
          '  delay(12);',
          '}',
          `${strip}.setBrightness(${percentToPwm(beginnerLedState.pixelBrightnessPercent)});`,
          '',
        ];
        break;
      case 'SPARKLE':
        effectCode = [
          `${strip}.clear();`,
          `int pixel = random(${strip}.numPixels());`,
          `${strip}.setPixelColor(pixel, ${strip}.Color(255, 255, 255));`,
          `${strip}.show();`,
          'delay(40);',
          `${strip}.setPixelColor(pixel, 0);`,
          `${strip}.show();`,
          '',
        ];
        break;
      case 'SCANNER':
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `  ${strip}.clear();`,
          `  ${strip}.setPixelColor(i, ${strip}.Color(255, 0, 0));`,
          `  ${strip}.show();`,
          '  delay(40);',
          '}',
          `for (int i = ${strip}.numPixels() - 2; i >= 0; i--) {`,
          `  ${strip}.clear();`,
          `  ${strip}.setPixelColor(i, ${strip}.Color(255, 0, 0));`,
          `  ${strip}.show();`,
          '  delay(40);',
          '}',
          '',
        ];
        break;
      case 'COMET':
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels() + 4; i++) {`,
          `  ${strip}.clear();`,
          '  for (int t = 0; t < 4; t++) {',
          '    int idx = i - t;',
          `    if (idx >= 0 && idx < ${strip}.numPixels()) {`,
          `      ${strip}.setPixelColor(idx, ${strip}.Color(255 - t * 60, 40, 180));`,
          '    }',
          '  }',
          `  ${strip}.show();`,
          '  delay(45);',
          '}',
          '',
        ];
        break;
      case 'RANDOM_COLORS':
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `  ${strip}.setPixelColor(i, ${strip}.Color(random(256), random(256), random(256)));`,
          '}',
          `${strip}.show();`,
          'delay(150);',
          '',
        ];
        break;
      case 'RAINBOW':
      default:
        effectCode = [
          `for (int i = 0; i < ${strip}.numPixels(); i++) {`,
          `  ${strip}.setPixelColor(i, skyroverPixelWheel((i * 256) / ${strip}.numPixels()));`,
          '}',
          `${strip}.show();`,
          '',
        ];
        break;
    }

    return statementPrefix(block, effectCode.join('\n'));
  };
}

registerBeginnerLedBlockGenerators();
